import { useState, useEffect, useMemo, useRef } from 'react';
import { getMenuData, subscribe } from '../store/menuStore';
import { formatNavMenuData, getEmptyMenuState } from '../utils/menuFormatter';

/**

 * Re-renders only when this menuId's data actually changes; avoids redundant renders.
 *
 * @param {string} menuId - The menu ID to fetch
 * @returns {object} Formatted menu data or empty state
 */
export const useMenuData = (menuId) => {
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const dataRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribe(menuId, () => {
      const next = getMenuData(menuId);
      if (next === dataRef.current) return;
      dataRef.current = next;
      setUpdateTrigger((prev) => prev + 1);
    });
    // If data was already in the store trigger a read so we don't stay on empty state
    const existing = getMenuData(menuId);
    if (existing && existing !== dataRef.current) {
      dataRef.current = existing;
      setUpdateTrigger((prev) => prev + 1);
    }
    return unsubscribe;
  }, [menuId]);

  return useMemo(() => {
    const menu = getMenuData(menuId);
    const formatted = menu
      ? formatNavMenuData(menuId, menu)
      : getEmptyMenuState(menuId);
    dataRef.current = menu;
    return formatted;
  }, [menuId, updateTrigger]);
};
